import { gql } from "@apollo/client";

export const GET_INVOICE = gql`
  query invoice($id: ID!) {
    invoice(id: $id) {
      id
      invoiceNumber
      invoiceName
      purchaseOrderId
      primaryRecipient {
        id
        firstName
        lastName
        businessName
        email
        phone
        contactType
        address {
          addressName
          address1
          address2
          zipcode
          state
          city
          country
        }
      }
      secondaryRecipients {
        id
        firstName
        lastName
        businessName
        email
        phone
        contactType
      }
      details {
        id
        invoiceId
        order
        productOrService
        description
        amount
        total
        quantity
      }
      invoiceDate
      sentDate
      sentBy {
        id
        firstName
        lastName
      }
      sentHistory {
        sentDate
        sentType
        sentBy {
          id
          firstName
          lastName
        }
      }
      estimateActionHistory {
        timestamp
        updatedBy {
          id
          firstName
          lastName
          businessName
          contactType
        }
        action
        comment
      }
      paymentError {
        code
        message
      }
      microdepositPending
      submittedBy {
        id
        firstName
        lastName
      }
      paymentTerms
      createdAt
      dueDate
      paidDate
      paidBy {
        id
        firstName
        lastName
        businessName
        contactType
      }
      subtotal
      discount
      discountType
      discountAmount
      tax
      taxType
      taxAmount
      serviceFee
      serviceFeeType
      serviceFeeAmount
      total
      status
      note
      transactionId
      transaction {
        id
        amount
        refundAmount
        type
        card {
          type
        }
        status
        fees {
          feeType
          feeAmount
        }
      }
      transactions {
        id
        amount
        refundAmount
        type
        card {
          type
        }
        status
      }
      refundTransactions {
        id
        amount
        refundAmount
        type
        card {
          type
        }
        status
        transactionDate
      }
      allocationType {
        key
        name
      }
      allocationName {
        id
        allocationCode
      }
      companyDetails {
        companyName
        companyPhone
        companyWebsite
        companyLogoUrl
        companyEmail
      }
      disputeData {
        disputeAmount
        disputeId
        disputeStatus
      }
      canceledBy {
        id
        firstName
        lastName
      }
      canceledDate
      customFields {
        fieldSettingId
        values
      }
    }
  }
`;

export const GET_INVOICES = gql`
  query invoices(
    $first: Int
    $after: String
    $status: [InvoiceStatusEnum]
    $searchTerm: String
    $sortDirection: SortDirectionEnum
    $sortColumn: String
  ) {
    invoices(
      status: $status
      first: $first
      after: $after
      searchTerm: $searchTerm
      sortDirection: $sortDirection
      sortColumn: $sortColumn
    ) {
      edges {
        cursor
        node {
          id
          invoiceNumber
          invoiceName
          purchaseOrderId
          primaryRecipient {
            id
            firstName
            lastName
            businessName
            email
            phone
            contactType
            address {
              addressName
              address1
              address2
              zipcode
              state
              city
              country
            }
          }
          secondaryRecipients {
            id
            firstName
            lastName
            businessName
            email
            phone
            contactType
          }
          details {
            id
            invoiceId
            order
            productOrService
            description
            amount
            total
            quantity
          }
          invoiceDate
          sentDate
          sentBy {
            id
            firstName
            lastName
          }
          sentHistory {
            sentDate
            sentType
            sentBy {
              id
              firstName
              lastName
            }
          }
          submittedBy {
            id
            firstName
            lastName
          }
          paymentTerms
          dueDate
          paidDate
          paidBy {
            id
            firstName
            lastName
            businessName
            contactType
          }
          subtotal
          discount
          discountType
          discountAmount
          tax
          taxType
          taxAmount
          serviceFee
          serviceFeeType
          serviceFeeAmount
          total
          status
          microdepositPending
          createdAt
          note
          transactionId
          allocationType {
            key
            name
          }
          allocationName {
            id
            allocationCode
          }
          companyDetails {
            companyName
            companyPhone
            companyWebsite
            companyLogoUrl
            companyEmail
          }
          disputeData {
            disputeAmount
            disputeId
            disputeStatus
          }
          canceledBy {
            id
            firstName
            lastName
          }
          canceledDate
          transaction {
            id
            status
            refundAmount
          }
          refundTransactions {
            id
            amount
            transactionDate
          }
          primaryRecipient {
            id
            firstName
            lastName
            businessName
            email
            phone
            contactType
          }
        }
      }
      pageInfo {
        hasNextPage
        endCursor
      }
    }
  }
`;

export const GET_MOST_RECENT_INVOICE_NUMBER = gql`
  query mostRecentInvoiceNumber($increment: Boolean) {
    mostRecentInvoiceNumber(increment: $increment)
  }
`;

export const DOES_INVOICE_NUMBER_EXIST = gql`
  query doesInvoiceNumberExist($invoiceId: ID, $invoiceNumber: String!) {
    doesInvoiceNumberExist(invoiceId: $invoiceId, invoiceNumber: $invoiceNumber)
  }
`;

export const CREATE_INVOICE = gql`
  mutation createInvoice($input: AddInvoiceInput!) {
    createInvoice(input: $input) {
      id
    }
  }
`;

export const CANCEL_INVOICE = gql`
  mutation cancelInvoice($id: ID!) {
    cancelInvoice(id: $id) {
      id
    }
  }
`;

export const UNCANCEL_INVOICE = gql`
  mutation uncancelInvoice($id: ID!) {
    uncancelInvoice(id: $id) {
      id
    }
  }
`;

export const UPDATE_INVOICE = gql`
  mutation updateInvoice($id: ID!, $input: UpdateInvoiceInput!) {
    updateInvoice(id: $id, input: $input) {
      id
      dueDate
      status
      invoiceDate
      invoiceNumber
      sentDate
      paymentTerms
      allocationId
      purchaseOrderId
      total
      createdAt
      note
      subtotal
      discount
      discountType
      discountAmount
      tax
      taxType
      taxAmount
      serviceFee
      serviceFeeType
      serviceFeeAmount
      updatedAt
      invoiceName
    }
  }
`;

export const SEND_INVOICE = gql`
  mutation sendInvoice($id: ID!, $sendAsEstimate: Boolean) {
    sendInvoice(id: $id, sendAsEstimate: $sendAsEstimate) {
      id
      status
      sentBy {
        id
        firstName
        lastName
      }
      sentDate
    }
  }
`;

export const RESEND_FAILED_PAYMENT_NOTICE = gql`
  mutation resendFailedPaymentNotice($id: ID!) {
    resendFailedPaymentNotice(id: $id) {
      id
      status
    }
  }
`;

export const REFUND_INVOICE = gql`
  mutation refundInvoice($id: ID!, $input: RefundInvoiceInput) {
    refundInvoice(id: $id, input: $input)
  }
`;

// Invoice Details Below

export const GET_INVOICE_DETAILS = gql`
  query invoiceDetails($invoiceId: ID!) {
    invoiceDetails(invoiceId: $invoiceId) {
      id
      invoiceId
      order
      productOrService
      description
      amount
      total
      quantity
    }
  }
`;

export const UPDATE_INVOICE_DETAILS = gql`
  mutation updateInvoiceDetails($input: UpdateInvoiceDetailsInput!) {
    updateInvoiceDetails(input: $input) {
      id
      order
      productOrService
      description
      amount
      total
      quantity
    }
  }
`;
